// Synthesized game audio (Web Audio, no files). Presentation only: it reads run state and events and
// never feeds anything back into the simulation, so its randomness (Math.random) is allowed here.

import type { EnvironmentDef } from '../config/environments';

export type AmbienceKind = EnvironmentDef['ambience'];

const MASTER = 0.7;
const ENGINE_GAIN = 0.22;

/** Engine voice: two detuned saws and a square sub through a waveshaper and a low-pass, pitched by RPM. */
class EngineVoice {
  private readonly oscs: OscillatorNode[];
  private readonly filter: BiquadFilterNode;
  private readonly out: GainNode;
  private rpm = 0;

  constructor(private readonly ctx: AudioContext, dest: AudioNode, private readonly character: { base: number; span: number; bright: number }) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.Q.value = 4;
    const shaper = ctx.createWaveShaper();
    shaper.curve = driveCurve(2.2);
    this.oscs = (['sawtooth', 'sawtooth', 'square'] as OscillatorType[]).map((type, i) => {
      const o = ctx.createOscillator();
      o.type = type;
      const g = ctx.createGain();
      g.gain.value = i === 2 ? 0.5 : 0.35;
      o.connect(g).connect(shaper);
      o.start();
      return o;
    });
    shaper.connect(this.filter).connect(this.out).connect(dest);
  }

  /** rpm in [0, 1]; load in [0, 1] (throttle) shapes loudness and brightness. */
  update(rpm: number, load: number, running: boolean): void {
    const t = this.ctx.currentTime;
    this.rpm += (rpm - this.rpm) * 0.25;
    const f = this.character.base + this.character.span * this.rpm;
    this.oscs[0].frequency.setTargetAtTime(f, t, 0.05);
    this.oscs[1].frequency.setTargetAtTime(f * 1.012, t, 0.05);
    this.oscs[2].frequency.setTargetAtTime(f * 0.5, t, 0.05);
    this.filter.frequency.setTargetAtTime(this.character.bright * (0.6 + 1.6 * this.rpm + 0.8 * load), t, 0.06);
    const level = running ? ENGINE_GAIN * (0.45 + 0.4 * load + 0.25 * this.rpm) : 0;
    this.out.gain.setTargetAtTime(level, t, running ? 0.08 : 0.3);
  }

  dispose(): void {
    this.out.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
    for (const o of this.oscs) o.stop(this.ctx.currentTime + 0.3);
  }
}

/** Looping environment bed: filtered noise with a slow swell, plus occasional synthesized birds or horns. */
class Ambience {
  private readonly nodes: AudioScheduledSourceNode[] = [];
  private readonly out: GainNode;
  private timer: number | null = null;

  constructor(private readonly ctx: AudioContext, dest: AudioNode, private readonly kind: AmbienceKind, noise: AudioBuffer) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.gain.setTargetAtTime(1, ctx.currentTime, 1.2);
    this.out.connect(dest);
    const bed = { terraces: [700, 0.05], highlands: [500, 0.08], village: [300, 0.06], beach: [900, 0.16], volcano: [380, 0.14] }[kind];
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = kind === 'beach' ? 'lowpass' : 'bandpass';
    filter.frequency.value = bed[0];
    filter.Q.value = 0.6;
    const level = ctx.createGain();
    level.gain.value = bed[1];
    // Slow swell: surf on the beach, gusts on the volcano and in the highlands.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = kind === 'beach' ? 0.12 : 0.07;
    const depth = ctx.createGain();
    depth.gain.value = bed[1] * (kind === 'beach' || kind === 'volcano' ? 0.9 : 0.4);
    lfo.connect(depth).connect(level.gain);
    src.connect(filter).connect(level).connect(this.out);
    src.start();
    lfo.start();
    this.nodes.push(src, lfo);
    this.scheduleDetail();
  }

  private scheduleDetail(): void {
    const wait = 1500 + Math.random() * 4000;
    this.timer = window.setTimeout(() => {
      if (this.kind === 'terraces' || this.kind === 'highlands') bird(this.ctx, this.out, this.kind === 'highlands' ? 0.5 : 1);
      else if (this.kind === 'village' && Math.random() < 0.5) horn(this.ctx, this.out);
      else if (this.kind === 'village') bird(this.ctx, this.out, 0.6);
      else if (this.kind === 'beach' && Math.random() < 0.4) gull(this.ctx, this.out);
      this.scheduleDetail();
    }, wait);
  }

  dispose(): void {
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.out.gain.setTargetAtTime(0, this.ctx.currentTime, 0.4);
    for (const n of this.nodes) n.stop(this.ctx.currentTime + 2);
  }
}

/**
 * The game's sound: an RPM-following engine, event one-shots and an ambience bed per environment.
 * The AudioContext starts on the first user gesture (browser autoplay rules). Mute is remembered.
 */
export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private engine: EngineVoice | null = null;
  private ambience: Ambience | null = null;
  private noise: AudioBuffer | null = null;
  private ambienceKind: AmbienceKind = 'terraces';
  private engineCharacter = { base: 42, span: 120, bright: 520 };
  private lowFuelAt = 0;
  muted: boolean;

  constructor() {
    this.muted = readMuted();
    const unlock = () => {
      this.start();
      if (this.ctx?.state === 'running') {
        window.removeEventListener('pointerdown', unlock, true);
        window.removeEventListener('keydown', unlock, true);
      }
    };
    window.addEventListener('pointerdown', unlock, true);
    window.addEventListener('keydown', unlock, true);
    document.addEventListener('visibilitychange', () => this.applySuspend());
  }

  get state(): string {
    return this.ctx?.state ?? 'idle';
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    writeMuted(muted);
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(muted ? 0 : MASTER, this.ctx.currentTime, 0.05);
  }

  setAmbience(kind: AmbienceKind): void {
    this.ambienceKind = kind;
    if (!this.ctx || !this.master || !this.noise) return;
    this.ambience?.dispose();
    this.ambience = new Ambience(this.ctx, this.master, kind, this.noise);
  }

  /** Engine sound per vehicle: base pitch, rev range and brightness. */
  setEngine(character: { base: number; span: number; bright: number }): void {
    this.engineCharacter = character;
    if (!this.ctx || !this.master) return;
    this.engine?.dispose();
    this.engine = new EngineVoice(this.ctx, this.master, character);
  }

  /** Called every frame with the run's state. */
  updateEngine(rpm: number, load: number, running: boolean): void {
    this.engine?.update(rpm, load, running);
  }

  /** A short repeating beep while fuel is low (rate-limited here, called every frame). */
  lowFuel(active: boolean): void {
    if (!active || !this.ctx) return;
    const now = this.ctx.currentTime;
    if (now < this.lowFuelAt) return;
    this.lowFuelAt = now + 1.4;
    this.blip(880, 0.06, 'square', 0.05);
    this.blip(660, 0.08, 'square', 0.05, 0.12);
  }

  coin(): void {
    this.blip(1320, 0.07, 'sine', 0.18);
    this.blip(1980, 0.12, 'sine', 0.14, 0.06);
  }

  gasCan(): void {
    for (let i = 0; i < 3; i++) this.sweep(260 - i * 30, 140 - i * 20, 0.09, 'sine', 0.22, i * 0.08);
    this.blip(1046, 0.18, 'triangle', 0.1, 0.24);
  }

  flip(): void {
    this.noiseSweep(400, 2400, 0.35, 0.16);
    this.arpeggio([784, 988, 1175], 0.07, 'triangle', 0.14, 0.2);
  }

  airtime(): void {
    this.blip(1568, 0.15, 'triangle', 0.1);
  }

  landing(strength: number): void {
    const s = Math.min(1, strength);
    this.sweep(120, 45, 0.18, 'sine', 0.35 * s);
    this.noiseBurst(0.12, 600, 0.18 * s);
  }

  crash(): void {
    this.noiseBurst(0.6, 1800, 0.5);
    this.sweep(180, 40, 0.5, 'sawtooth', 0.25);
    this.blip(310, 0.25, 'square', 0.08, 0.05);
    this.blip(233, 0.3, 'square', 0.06, 0.12);
  }

  /** School bell for a delivery. */
  delivery(): void {
    this.arpeggio([1046, 1318, 1568, 2093], 0.09, 'triangle', 0.18);
  }

  click(): void {
    this.blip(1800, 0.025, 'square', 0.05);
  }

  purchase(): void {
    this.arpeggio([1175, 1568], 0.06, 'square', 0.08);
    this.noiseBurst(0.08, 5000, 0.06, 0.1);
  }

  /** Fanfare for a newly unlocked environment, vehicle or a finished mission. */
  fanfare(): void {
    this.arpeggio([523, 659, 784, 1046, 1318], 0.09, 'triangle', 0.16);
  }

  private start(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : MASTER;
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.ratio.value = 4;
      this.master.connect(comp).connect(this.ctx.destination);
      this.noise = makeNoise(this.ctx);
      this.engine = new EngineVoice(this.ctx, this.master, this.engineCharacter);
      this.ambience = new Ambience(this.ctx, this.master, this.ambienceKind, this.noise);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private applySuspend(): void {
    if (!this.ctx) return;
    if (document.hidden) void this.ctx.suspend();
    else void this.ctx.resume();
  }

  private voice(gain: number, delay: number, length: number): { g: GainNode; t: number } | null {
    if (!this.ctx || !this.master || this.muted) return null;
    const t = this.ctx.currentTime + delay;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + length);
    g.connect(this.master);
    return { g, t };
  }

  private blip(freq: number, length: number, type: OscillatorType, gain: number, delay = 0): void {
    const v = this.voice(gain, delay, length);
    if (!v || !this.ctx) return;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    o.connect(v.g);
    o.start(v.t);
    o.stop(v.t + length + 0.02);
  }

  private sweep(from: number, to: number, length: number, type: OscillatorType, gain: number, delay = 0): void {
    const v = this.voice(gain, delay, length);
    if (!v || !this.ctx) return;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(from, v.t);
    o.frequency.exponentialRampToValueAtTime(to, v.t + length);
    o.connect(v.g);
    o.start(v.t);
    o.stop(v.t + length + 0.02);
  }

  private arpeggio(freqs: number[], step: number, type: OscillatorType, gain: number, delay = 0): void {
    freqs.forEach((f, i) => this.blip(f, step * 2.2, type, gain, delay + i * step));
  }

  private noiseBurst(length: number, cutoff: number, gain: number, delay = 0): void {
    const v = this.voice(gain, delay, length);
    if (!v || !this.ctx || !this.noise) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = cutoff;
    src.connect(f).connect(v.g);
    src.start(v.t, Math.random());
    src.stop(v.t + length + 0.02);
  }

  private noiseSweep(from: number, to: number, length: number, gain: number): void {
    const v = this.voice(gain, 0, length);
    if (!v || !this.ctx || !this.noise) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const f = this.ctx.createBiquadFilter();
    f.type = 'bandpass';
    f.Q.value = 2;
    f.frequency.setValueAtTime(from, v.t);
    f.frequency.exponentialRampToValueAtTime(to, v.t + length);
    src.connect(f).connect(v.g);
    src.start(v.t, Math.random());
    src.stop(v.t + length + 0.02);
  }
}

function makeNoise(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

function driveCurve(amount: number): Float32Array<ArrayBuffer> {
  const n = 256;
  const curve = new Float32Array(new ArrayBuffer(n * 4));
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * amount);
  }
  return curve;
}

function chirp(ctx: AudioContext, dest: AudioNode, t: number, from: number, to: number, length: number, gain: number): void {
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(from, t);
  o.frequency.exponentialRampToValueAtTime(to, t + length);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + length);
  o.connect(g).connect(dest);
  o.start(t);
  o.stop(t + length + 0.02);
}

function bird(ctx: AudioContext, dest: AudioNode, level: number): void {
  const t = ctx.currentTime;
  const base = 2600 + Math.random() * 1400;
  const notes = 2 + Math.floor(Math.random() * 4);
  for (let i = 0; i < notes; i++) chirp(ctx, dest, t + i * 0.11, base, base * (1.2 + Math.random() * 0.3), 0.08, 0.035 * level);
}

function gull(ctx: AudioContext, dest: AudioNode): void {
  const t = ctx.currentTime;
  for (let i = 0; i < 3; i++) chirp(ctx, dest, t + i * 0.28, 1500, 900, 0.22, 0.03);
}

function horn(ctx: AudioContext, dest: AudioNode): void {
  // A distant moped horn: two short square beeps, low-passed and quiet.
  const t = ctx.currentTime;
  const f = ctx.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = 1200;
  f.connect(dest);
  for (let i = 0; i < 2; i++) {
    const o = ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 520;
    const g = ctx.createGain();
    const s = t + i * 0.22;
    g.gain.setValueAtTime(0.0001, s);
    g.gain.exponentialRampToValueAtTime(0.02, s + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, s + 0.15);
    o.connect(g).connect(f);
    o.start(s);
    o.stop(s + 0.17);
  }
}

function readMuted(): boolean {
  try {
    return window.localStorage.getItem('mbg-climb:muted') === '1';
  } catch {
    return false;
  }
}

function writeMuted(muted: boolean): void {
  try {
    window.localStorage.setItem('mbg-climb:muted', muted ? '1' : '0');
  } catch {
    // Mute lasts for this session only.
  }
}
